/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createTheme } from "@material-ui/core/styles";

const deleteSvgIcon = "/icons/delete-red.svg";
const deleteWhiteSvgIcon = "/icons/delete-white.svg";
const editSvg = "/icons/edit.svg";
const addSvg = "/icons/add.svg";

declare module "@material-ui/core/styles/createTheme" {
  interface Theme {
    custom: any;
  }
  interface ThemeOptions {
    custom?: any;
  }
}

export const systemTheme = createTheme({
  palette: {
    background: {
      paper: "#ffffff",
      default: "#ffffff",
    },
    secondary: {
      light: "#f7f8fb",
      main: "#8d91a3",
    },
    primary: {
      main: "#4050b5",
    },
    text: {
      primary: "#1D2028",
      secondary: "#0000008a",
    },
    grey: {
      "100": "#eff1f5",
    },
    common: {
      black: "#000000",
      white: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: "Gilmer",
    h5: {
      fontSize: 13,
      fontWeight: 600,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: 1.08,
      letterSpacing: "normal",
    },
    body1: {
      fontSize: 13,
      fontWeight: "normal",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: 1.85,
      letterSpacing: "normal",
    },
    body2: {
      fontSize: 12,
      fontWeight: "normal",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: 2,
      letterSpacing: "normal",
    },
  },
  custom: {
    login: {
      loginBg: {
        backgroundColor: "#5567D5",
        minHeight: `calc(100vh - 49px)`,
        width: "100%",
        textAlign: "left",
      },
      logo: {
        width: 117,
        src: "themes/default/images/logo-white.svg",
      },
      tagLine: {
        color: "#181c20",
        fontSize: 17,
        fontWeight: "bold",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: 1.6,
        letterSpacing: "normal",
      },
      google: {
        color: "rgba(0,0,0,0.8)",
        width: 264,
      },
      github: {
        color: "rgba(0,0,0,0.8)",
        width: 264,
      },
      microsoft: {
        color: "rgba(0,0,0,0.8)",
        width: 264,
      },
      email: {
        color: "rgba(0,0,0,0.8)",
        width: 264,
      },
      enterprise: {
        color: 'rgba(0,0,0,0.8)',
      },
      continueEnterpriseLoginBtn: {
        fontFamily: 'Roboto',
        paddingLeft: 8,
        paddingRight: 8,
        height: 40,
        width: 224,
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.5,
        borderRadius: 2,
        letterSpacing: 0.22,
        color: 'rgba(0,0,0,0.8)',
        boxShadow: '0 0 1px 0 rgba(0,0,0,0.12), 0 1px 1px 0 rgba(0,0,0,0.24)',
        marginTop: '12px',
        backgroundColor: '#FFFFFF',
        whiteSpace: 'nowrap',
        '&:hover, &:focus, &:active': {
          backgroundColor: '#EEEEEE',
          boxShadow: '0 0 2px 0 rgba(0,0,0,0.12), 0 2px 2px 0 rgba(0,0,0,0.24)',
        },
      },
      enterpriseGoBackLink: {
        marginTop: '12px',
        marginBottom: '73px',
      },
      loginBtn: {
        fontFamily: "Roboto",
        paddingLeft: 8,
        paddingRight: 12,
        height: 40,
        textTransform: "none",
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.5,
        borderRadius: 2,
        letterSpacing: 0.22,
        boxShadow: "0 0 1px 0 rgba(0,0,0,0.12), 0 1px 1px 0 rgba(0,0,0,0.24)",
        whiteSpace: "nowrap",
        "&:hover, &:focus, &:active": {
          backgroundColor: "#EEEEEE",
          boxShadow: "0 0 2px 0 rgba(0,0,0,0.12), 0 2px 2px 0 rgba(0,0,0,0.24)",
        },
      },
      logoTitle: {
        flexGrow: 1,
        paddingLeft: "15px",
        paddingRight: "15px",
        fontWeight: "bold",
        textAlign: "left",
        fontSize: "25px",
        lineHeight: 1.55,
      },
      successMsg: {
        display: "inline",
        fontSize: "15px",
      },
      divider: {
        marginLeft: "15px",
      },
      loginWrapper: {
        backgroundColor: "#F7F8FB",
      },
    },
    appBar: {
      styles: {
        boxShadow: "inset 0 -1px 0 0 #E1E2E7, 0 1px 4px 0 rgba(0,0,0,0.08)",
        backgroundColor: "#FFFFFF",
      },
      title: {
        text: "API Developer Portal",
        styles: {
          color: "#40404B",
          fontSize: "14px",
          fontFamily: "GilmerMedium",
          paddingLeft: "15px",
          paddingRight: "15px",
          textAlign: "left",
          lineHeight: "17px",
        },
      },
      logo: {
        src: "/themes/default/images/logo-black.svg",
        styles: {
          paddingRight: "15px",
          width: "auto",
          height: "auto",
          cursor: "pointer",
        },
      },
      menuButtons: {
        textTransform: "capitalize",
        fontSize: "13px",
        lineHeight: "24px",
        color: "#222228",
      },
      menuButtonsSelected: {
        color: "#5567D5",
      },
      signInButton: {
        textTransform: "capitalize",
        color: "#222228",
        fontFamily: "GilmerRegular",
        fontSize: "13px",
        paddingLeft: "15px",
        paddingRight: "15px",
      },
      orgSelect: {
        color: "#5567d5",
      },
    },
    footer: {
      styles: {
        height: "48px",
        marginTop: "auto",
        borderTop: "1px solid",
        borderTopColor: "#e4e8ee7d",
        color: "#8d91a3",
        backgroundColor: "#ffffff",
      },
      organization: {
        name: "WSO2",
        url: "organizationUrl",
      },
      itemStyles: {
        fontSize: "12px",
        lineHeight: "24px",
        letterSpacing: "normal",
        color: "#8D91A3",
        paddingRight: "24px",
        cursor: "pointer",
      },
      items: [
        {
          text: "Terms of Use",
          url: "https://wso2.com/choreo/terms-of-use/",
        },
        {
          text: "Privacy Policy",
          url: "https://wso2.com/privacy-policy",
        },
        {
          text: "Support",
          url: "https://wso2-choreo.slack.com/join/shared_invite/zt-qm86tg1f-R2kXf~26GB6X6Ux0GcuYbQ",
        },
      ],
      logo: {
        src: "",
        alt: "WSO2 Logo",
      },
      loginFooter: {
        backgroundColor: "#F7F8FB",
      },
    },
    homePage: {
      styles: {
        backgroundColor: "#f7f8fb",
      },
      title: {
        text: "Continue working with APIs",
        styles: {
          fontFamily: "GilmerBold",
          fontSize: "42px",
          lineHeight: "48px",
          color: "#1D2028",
          width: "470px",
          textAlign: "left",
          marginBottom: "34px",
        },
      },
      subTitle: {
        text: "Use our APIs to automate, innovate, and connect to millions of customers.",
        styles: {
          fontFamily: "Gilmer",
          fontSize: "15px",
          lineHeight: "24px",
          color: "#1D2028",
          width: "470px",
          textAlign: "left",
          marginBottom: "67px",
        },
      },
      browseButton: {
        text: "Browse APIs",
        styles: {
          fontSize: "13px",
          lineHeight: "16px",
          textTransform: "capitalize",
          width: "179px",
          height: "48px",
          borderRadius: "25px",
          backgroundColor: "primary",
          color: "primary",
          "&:hover": {
            backgroundColor: "#303f9f",
          },
        },
      },
      banner: {
        src: "themes/default/images/banner.svg",
      },
      sections: {
        displayOrder: [
          "IntroVideo",
          "FeaturedApis",
          "OrganizationInfo",
          "UseCases",
          "DeveloperBlogs",
        ],
        introVideo: {
          enabled: false,
          videoUrl: "https://www.youtube.com/embed/pax5EJFM2mM",
          title: {
            text: "Getting Started with WSO2 Developer Platform APIs",
            styles: {
              fontFamily: "GilmerBold",
              fontSize: "29px",
              lineHeight: "64px",
              color: "#222228",
              textAlign: "center",
              paddingTop: "80px",
            },
          },
          subTitle: {
            text: "Build the future with us. Our APIs enable you to automate, innovate, and connect to millions of WSO2 Developer Platform customers",
            styles: {
              fontFamily: "Gilmer",
              fontSize: "16px",
              lineHeight: "32px",
              color: "#40404B",
              textAlign: "center",
              marginBottom: "67px",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "782px",
            },
          },
          videoIFrame: {
            height: "501px",
            width: "813px",
            border: "5px solid #FFFFFF",
            borderRadius: "12px",
            boxShadow: "0 30px 80px -30px rgba(32,39,86,0.25)",
          },
        },
        featuredApis: {
          enabled: false,
          apiListQuery: "/apis?query=api-category:featured",
          title: {
            text: "Featured APIs",
            styles: {
              fontFamily: "GilmerBold",
              fontSize: "29px",
              lineHeight: "64px",
              color: "#222228",
              textAlign: "center",
              paddingTop: "80px",
            },
          },
          subTitle: {
            text: "View Featured WSO2 Developer Platform APIs or browse to find the APIs that meet your specific needs",
            styles: {
              fontFamily: "Gilmer",
              fontSize: "16px",
              lineHeight: "32px",
              color: "#40404B",
              textAlign: "center",
              marginBottom: "67px",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "782px",
            },
          },
          browseButton: {
            text: "Browse all APIs",
            styles: {
              fontSize: "13px",
              lineHeight: "16px",
              textTransform: "capitalize",
              width: "179px",
              height: "48px",
              borderRadius: "25px",
              color: "#40404B",
              backgroundColor: "#f7f8fb",
              margin: "78px",
              fontFamily: "GilmerMedium",
              "&:hover": {
                backgroundColor: "#f7f8fb",
              },
            },
          },
          noApisImage: {
            src: "../themes/default/images/no-apis.svg",
          },
        },
        organization: {
          enabled: false,
          styles: {
            maxWidth: "1640px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            marginTop: "0px",
          },
          title: {
            text: "Why WSO2 Developer Platform",
            styles: {
              fontFamily: "GilmerBold",
              fontSize: "29px",
              lineHeight: "64px",
              color: "#222228",
              textAlign: "center",
              paddingTop: "80px",
            },
          },
          subTitle: {
            text: "Build the future with us. Our APIs enable you to automate, innovate, and connect to millions of WSO2 Developer Platform customers",
            styles: {
              fontFamily: "Gilmer",
              fontSize: "16px",
              lineHeight: "32px",
              color: "#40404B",
              textAlign: "center",
              marginBottom: "67px",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "782px",
            },
          },
          cardStyles: {
            title: {
              fontSize: "16px",
              fontFamily: "GilmerBold",
              lineHeight: "24px",
              color: "#181C20",
              textAlign: "left",
              marginBottom: "16px",
            },
            content: {
              fontSize: "14px",
              fontFamily: "Gilmer",
              lineHeight: "24px",
              color: "#1D2028",
              textAlign: "left",
            },
            icon: {
              marginBottom: "16px",
              width: "48px",
              height: "auto",
            },
          },
          cards: [
            {
              title: "Built for Ballerina",
              content:
                "To code integrations in WSO2 Developer Platform, you must use Ballerina. Ballerina is an open source programming language that is purpose-built for integrations. It comes with unique and powerful features like network abstractions, a sequence-diagram-based programming model and in-built cloud orchestration.<br> In Ballerina, every program is a sequence diagram that illustrates distributed and concurrent interactions between remote endpoints automatically.",
              icon: "themes/default/images/built.svg",
            },
            {
              title: "No compromise between code and low-code ",
              content:
                "In Ballerina, every program is a sequence diagram that illustrates distributed and concurrent interactions between remote endpoints automatically.",
              icon: "themes/default/images/intelligent.svg",
            },
            {
              title: "Best practices",
              content:
                "Building integration software is not child&apos;s play. WSO2 Developer Platform re-think every phase of an integration project from development to observability ",
              icon: "themes/default/images/lowcode.svg",
            },
            {
              title: "WSO2 Developer Platform is intelligent",
              content:
                "WSO2 Developer Platform intelligently guide developers with varying levels of experience and expertise to produce high quality integration flows efficiently",
              icon: "themes/default/images/practice.svg",
            },
            {
              title: "Application flow monitoring",
              content:
                "What you have graphically developed is what you will observe Choreo’s application flow monitoring is unique, it traces to each control",
              icon: "themes/default/images/wysi.svg",
            },
          ],
        },
        useCases: {
          enabled: false,
          styles: {
            maxWidth: "1640px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            marginTop: "30px",
          },
          title: {
            text: "Use Cases",
            styles: {
              fontFamily: "GilmerBold",
              fontSize: "29px",
              lineHeight: "64px",
              color: "#222228",
              textAlign: "center",
              paddingTop: "80px",
            },
          },
          subTitle: {
            text: "Need inspiration? Check out real-world scenarios for using our APIs. The page will contain basic information about the organization and its API services",
            styles: {
              fontFamily: "Gilmer",
              fontSize: "16px",
              lineHeight: "32px",
              color: "#40404B",
              textAlign: "center",
              marginBottom: "67px",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "782px",
            },
          },
          cardStyles: {
            title: {
              fontSize: "16px",
              fontFamily: "GilmerBold",
              lineHeight: "24px",
              color: "#181C20",
              textAlign: "left",
              marginBottom: "16px",
            },
            content: {
              fontSize: "14px",
              fontFamily: "Gilmer",
              lineHeight: "24px",
              color: "#1D2028",
              textAlign: "left",
            },
            icon: {
              marginBottom: "16px",
              width: "48px",
              height: "48px",
            },
          },
          cards: [
            {
              title: "Finance",
              content:
                "An API Product/Group is an abstract collection of APIs grouped together to address a particular business function of the organization",
              icon: "themes/default/images/finance.svg",
            },
            {
              title: "Data and Analytics",
              content:
                "Through products/groups, a developer has access to the list of APIs which are relevant for a particular business operation.",
              icon: "themes/default/images/data-analytics.svg",
            },
            {
              title: "Security",
              content:
                "An API Product/Group is an abstract collection of APIs grouped together to address a particular business function of the organization",
              icon: "themes/default/images/security.svg",
            },
            {
              title: "Commercial",
              content:
                "Through products/groups, a developer has access to the list of APIs which are relevant for a particular business operation.",
              icon: "themes/default/images/commercial.svg",
            },
            {
              title: "Services",
              content:
                "An API Product/Group is an abstract collection of APIs grouped together to address a particular business function of the organization",
              icon: "themes/default/images/service.svg",
            },
          ],
        },
        developerBlog: {
          enabled: false,
          styles: {
            maxWidth: "1640px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            marginTop: "30px",
          },
          title: {
            text: "Developer Blog",
            styles: {
              fontFamily: "GilmerBold",
              fontSize: "29px",
              lineHeight: "64px",
              color: "#222228",
              textAlign: "center",
              paddingTop: "80px",
            },
          },
          subTitle: {
            text: "Our developer blog is a place for all business and IT developers",
            styles: {
              fontFamily: "Gilmer",
              fontSize: "16px",
              lineHeight: "32px",
              color: "#40404B",
              textAlign: "center",
              marginBottom: "67px",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "782px",
            },
          },
          cardStyles: {
            title: {
              fontSize: "16px",
              fontFamily: "GilmerBold",
              lineHeight: "24px",
              color: "#181C20",
              textAlign: "left",
              marginBottom: "16px",
            },
            author: {
              fontSize: "14px",
              fontFamily: "Gilmer",
              lineHeight: "24px",
              color: "#8D91A3",
              textAlign: "left",
              marginBottom: "16px",
            },
            content: {
              fontSize: "14px",
              fontFamily: "Gilmer",
              lineHeight: "24px",
              color: "#1D2028",
              textAlign: "left",
            },
            readMoreButton: {
              textTransform: "capitalize",
              color: "#5567D5",
              margin: "15px 0px 0px 0px",
              padding: "0px",
            },
          },
          cards: [
            {
              title: "Announcing the first preview of Ballerina Swan Lake",
              author: "John Doe",
              content:
                "We started work on Ballerina almost 4 years ago, in August 2016. We released jBallerina 1.0, which implemented the 2019-R3 specification, in September 2019 and since then have released minor version updates every quarter and patch releases every two weeks.",
              blogUrl: "https://wso2.com",
            },
            {
              title: "WSO2 Developer Platform IPaaS for Integration",
              author: "John Smith",
              content:
                "WSO2 Developer Platform is an iPaaS, which will give you a complete, AI-powered experience with code, low code, or both to integrate your applications",
              blogUrl: "https://wso2.com",
            },
            {
              title: "Low Code editor",
              author: "Sarah Fox",
              content:
                "The WSO2 Developer Platform platform lets you implement your integrations applications using the low code editor and you won&apos;t need to worry about complex codes",
              blogUrl: "https://wso2.com",
            },
          ],
        },
      },
    },
    apiListingPage: {
      styles: {
        backgroundColor: "#f7f8fb",
      },
      title: {
        text: "Find and Connect to Thousands of APIs",
        styles: {
          fontFamily: "GilmerBold",
          fontSize: "29px",
          lineHeight: "24px",
          color: "#222228",
        },
      },
      subTitle: {
        text: "Explore our APIs and discover how you can use them for your application",
        styles: {
          fontFamily: "Gilmer",
          fontSize: "15px",
          lineHeight: "64px",
          color: "#8D91A3",
        },
      },
      apiSearchBar: {
        textField: {
          borderRadius: "25px",
          width: "600px",
          height: "44px",
        },
        searchButton: {
          borderRadius: "25px",
          textTransform: "capitalize",
          fontSize: "12px",
          fontFamily: "GilmerMedium",
          backgroundColor: "#5567D5",
        },
      },
      tagCategory: {
        tagsTitle: "Tags",
        categoryTitle: "API Categories",
        styles: {
          fontFamily: "GilmerBold",
          fontSize: "15px",
          color: "#222228",
          margin: "16px 8px 8px 8px",
          textAlign: "left",
        },
      },
      noApisImage: {
        src: "../themes/default/images/no-apis.svg",
      },
      tagChip: {
        backgroundColor: "#a6b3ff",
      },
      apiTilePalette: {
        type: "light",
        background: {
          primary: {
            light: "#FFFFFF",
            dark: "#9A97EB"
          },
          secondary: {
            light: "#5567D5",
            dark: "#542C3A"
          }
        },
        text: {
          primary: {
            light: "#1D2028",
            dark: "#493AA5"
          },
          secondary: {
            light: "#8D91A3",
            dark: "#f50057"
          }
        },
        button: {
          primary: {
            light: "#F7F8FB",
            dark: "#303f9f"
          },
          secondary: {
            light: "#E0E2E9",
            dark: "#303f9f"
          }
        }
      },
    },
    appListingPage: {
      styles: {
        backgroundColor: "#f7f8fb",
      },
      title: {
        fontSize: "29px",
        lineHeight: "64px",
        fontFamily: "GilmerBold",
        color: "#222228",
      },
      subTitle: {
        color: "#8D91A3",
      },
      listHeaderTextStyle: {
        color: "#181C20",
        fontSize: "17px",
        lineHeight: "24px",
      },
      appDeleteButtonStyle: {
        color: "#EA4C4D",
        border: "1px solid",
        borderColor: "#F8C7C7",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left",
        backgroundImage: "url('../../themes/default/images/clear-red.svg')",
        "&:hover": {
          backgroundColor: "#FCEDED",
          color: "#EA4C4D",
          borderColor: "#EA4C4D",
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
        },
        "&:focus, &:active": {
          borderColor: "#EA4C4D",
          backgroundColor: "#EA4C4D !important",
          color: "#ffffff",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px",
          backgroundPosition: "left",
          backgroundImage: "url('../../themes/default/images/clear-white.svg')",
        },
      },
      deleteButtonTextStyle: {
        textTransform: "initial",
        fontSize: 11,
        fontWeight: 400,
      },
      hoverButtonStyle: {
        color: "#3f50b5",
        border: "1px solid",
        borderColor: "#CBCFF1",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left",
        "&:hover": {
          backgroundColor: "rgb(204, 209, 242, 0.3)",
          color: "#3f50b5",
          borderColor: "#A6B3FF",
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
        },
        "&:focus": {
          backgroundColor: `#3f50b5 !important`,
          color: "#ffffff",
          borderColor: "#3f50b5",
        },
      },
      createBtn: {},
      defaultAppThumbnail: {
        color: "#ffffff",
        backgroundColor: "#5567D5",
      },
      editButtonIcon: {
        backgroundImage: "url('../../themes/default/images/edit.svg')",
      },
      addButtonIcon: {
        "backgroundImage": "url('../../themes/default/images/add.svg')"
      },
      searchButtonStyles: {
        border: "1px solid #E6E7EC",
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
      loaderContainerStyles: {
        backgroundColor: "#ff7961",
      },
      appSearchFieldStyle: {
        "& .MuiTextField-root": {
          padding: "4px 3px 3px 3px",
          borderRadius: "21px",
          border: "solid 1px #e6e7ec",
          height: "40px",
          minWidth: "240px",
          "& :focus": {
            borderColor: "#cfd0d6",
          },
          "& input::placeholder": {
            color: "#2222284d",
          },
        },
        "& .MuiInput-root": {
          paddingLeft: "15px",
          marginBottom: "1px",
          marginRight: "8px",
          height: "40px",
          backgroundImage: 'url("../../themes/default/images/search.svg")',
          backgroundSize: "16px",
          backgroundPosition: "right",
          backgroundRepeat: "no-repeat",
          "& .MuiInputAdornment-positionStart .MuiInputAdornment-positionEnd": {
            display: "none",
          },
        },
        "& .MuiInputAdornment-positionEnd .MuiIconButton-root.Mui-disabled": {
          color: "transparent",
        },
      },
    },
    appLandingPage: {
      primaryTitleStyle: {
        textAlign: "center",
        fontFamily: "GilmerBold",
        fontSize: "29px",
        lineHeight: "64px",
        color: "#222228",
      },
      secondaryTitleStyle: {
        color: "#9399a8",
        textAlign: "center",
      },
    },
    apiOverviewPage: {
      leftMenu: {
        styles: {
          position: "relative",
          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "column",
          background: "#f7f8fb",
          width: "260px",
          borderRight: "none",
        },
        menuItems: {
          selected: {
            "&:hover": {
              backgroundColor: "transparent",
            },
            color: "#5567D5",
            padding: "15px",
            paddingTop: "10px",
            paddingBottom: "10px",
          },
          unSelected: {
            "&:hover": {
              backgroundColor: "transparent",
            },
            padding: "15px",
            paddingTop: "10px",
            paddingBottom: "10px",
          },
          textStyles: {
            fontSize: "13px",
            lineHeight: 1.85,
          },
          iconStyles: {
            minWidth: "28px",
          },
        },
      },
      tabs: {
        overview: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          apiIconImage: {
            margin: "auto",
            display: "flex",
            maxWidth: "100%",
            maxHeight: "100%",
            width: 97,
            height: 97,
            border: "1px solid #E6E7EC",
            borderRadius: "8px",
            boxSizing: "border-box",
            fontSize: "70px",
            backgroundColor: "#5567D5",
            fontFamily: "sans-serif",
          },
          apiIconImageBackground: {
            backgroundColor: "#ffffff",
          },
          apiName: {
            height: "24px",
            width: "auto",
            color: "#181C20",
            fontSize: "19px",
            letterSpacing: 0,
            lineHeight: "24px",
            fontFamily: "GilmerMedium",
          },
          apiVersionProvider: {
            height: "24px",
            width: "auto",
            color: "#8D91A3",
            fontSize: "13px",
            lineHeight: "24px",
          },
          apiDetailTitles: {
            fontSize: "14px",
            color: "#1D2028",
            letterSpacing: 0,
            fontFamily: "GilmerMedium",
          },
          apiDetailsContent: {
            textAlign: "left",
            color: "#40404B",
            fontSize: "14px",
            letterSpacing: 0,
            lineHeight: "24px",
          },
          apiRatingBox: {
            textAlign: "left",
            border: "1px solid #e8e8e8",
            borderRadius: "8px",
            margin: 0,
            width: "fit-content",
          },
          apiRatingTextCover: {
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            color: "#ababab",
          },
          apiRatingStars: {
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          },
          apiRatingRectangle: {
            height: "28px",
            width: "1px",
            opacity: 0.45,
            backgroundColor: "#D8D8D8",
          },
          apiRatingButton: {
            padding: 0,
            minWidth: "32px",
            height: "32px",
            textAlign: "center",
            backgroundColor: "#f1f1f1",
          },
          apiRatingText: {
            fontSize: "13px",
          },
          apiCommentsCellContent: {
            fontSize: "13px",
            paddingLeft: 0,
            overflowWrap: "anywhere",
          },
          apiCommentCreator: {
            fontSize: "13px",
            paddingLeft: 0,
            minWidth: "200px",
          },
          apiCommentsDeleteButton: {
            padding: "0 0 0 5px",
            backgroundColor: "inherit",
            "&:hover": {
              backgroundColor: "inherit",
            },
          },
          apiCommentDeleteConfirmText: {
            textAlign: "center",
            paddingBottom: 0,
          },
          apiDocumentsTitleBox: {
            textAlign: "left",
            marginTop: "33px",
            paddingLeft: 0,
            padding: 0,
          },
          apiDocumentsTitleText: {
            fontSize: "14px",
            color: "#1D2028",
            letterSpacing: 0,
            fontFamily: "GilmerMedium",
          },
          apiDocumentPropertyText: {
            fontSize: "13px",
            lineHeight: 1.85,
          },
          apiTagsTitleBox: {
            textAlign: "left",
            marginTop: "33px",
            paddingLeft: 0,
            padding: 0,
          },
          apiTagsTitleText: {
            fontSize: "14px",
            color: "#1D2028",
            letterSpacing: 0,
            fontFamily: "GilmerMedium",
          },
          apiTagsChip: {
            color: "#32324D",
            fontSize: "13px",
            letterSpacing: 0,
            backgroundColor: "#F7F8FB",
          },
          apiScopesTitleBox: {
            textAlign: "left",
            marginTop: "33px",
            paddingLeft: 0,
            padding: 0,
          },
          apiScopesTitleText: {
            fontSize: "14px",
            color: "#1D2028",
            letterSpacing: 0,
            fontFamily: "GilmerMedium",
          },
          apiScopesNoScopesMessage: {
            fontSize: "12px",
            fontStyle: "Italic",
          },
          apiScopesChip: {
            color: "#32324D",
            fontSize: "13px",
            letterSpacing: 0,
            backgroundColor: "#F7F8FB",
          },
          copyAllScopes: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "0px 8px",
            gap: "4px",
            position: 'absolute',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          scopesGrid: {
            position: "relative",
            width: "144px",
            height: "24px",
            display: "flex",
          },
          copyScopesText: {
            fontStyle:"normal",
            fontWeight: 400,
            fontSize: "13px",
            lineHeight: "24px",
            textAlign: "center",
            color: "#5567D5",
            flex: "none",
            order: 1,
            flexGrow: 0,
            paddingLeft: "4px",
          }
        },
        credentials: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          subTitle: {
            textAlign: "left",
            paddingTop: "20px",
            paddingBottom: "20px",
            fontFamily: "GilmerMedium",
            fontSize: "13px",
            lineHeight: "14px",
            color: "#1D2028",
          },
          subTitleContract: {
            textAlign: "left",
            paddingTop: "22px",
            paddingBottom: "5px",
            fontFamily: "GilmerMedium",
            fontSize: "13px",
            lineHeight: "14px",
            letterSpacing: "normal",
            color: "#1D2028",
          },
          rowLabelText: {
            borderWidth: 0,
            padding: 0,
            fontSize: "13px",
          },
          updateButton: {
            left: 520,
            textTransform: "capitalize",
            fontFamily: "GilmerMedium",
            fontSize: "12px",
            lineHeight: "16px",
            color: "#FFFFFF",
          },
          generateCredentialsButton: {
            left: 352,
            textTransform: "capitalize",
            fontFamily: "GilmerMedium",
            fontSize: "12px",
            lineHeight: "16px",
            color: "#FFFFFF",
          },
          errorTextStyle: {
            color: "red",
          },
          helperTextStyle: {
            marginLeft: 0,
            color: "#8D91A3",
            lineHeight: 1.6,
            maxWidth: 410,
          },
          contractSelectBoxStyle: {
            width: "100%",
            borderRadius: "5px",
            background: "linear-gradient(180deg, #ffffff 0%, #f7f7f9 100%)",
            boxShadow:
              "inset 0 0 0 1px #dee0e7, 0 1px 1px 0 rgba(0, 0, 0, 0.06)",
            cursor: "pointer",
            border: 1,
            "&:active": {
              background: "linear-gradient(180deg, #ffffff 0%, #f7f7f9 100%)",
              boxShadow:
                "inset 0 0 0 1px #a6b3ff, 0 1px 1px 0 rgba(0, 0, 0, 0.06)",
              border: "1px solid #5567d5",
            },
            "&:focused": {
              background: "linear-gradient(180deg, #ffffff 0%, #f7f7f9 100%)",
              boxShadow:
                "inset 0 0 0 1px #a6b3ff, 0 1px 1px 0 rgba(0, 0, 0, 0.06)",
              border: "1px solid #5567d5 !important",
            },
            "& .MuiSelect-selectMenu": {
              "& .TextSpan": {
                color: "#222228",
              },
            },
          },
          contractDropdownStyle: {
            backgroundColor: "#fff",
            boxSizing: "border-box",
            width: "auto",
            border: "1px solid #DEE0E7",
            borderRadius: "5px",
            boxShadow: "0 5px 10px -3px rgba(50,50,77,0.1)",
            color: "#222228",
            marginTop: "0.25rem",
          },
          grantTypeTextStyle: {
            fontSize: "13px",
          },
          generateAccessTokenButton: {
            textAlign: "center",
            fontFamily: "GilmerMedium",
            fontSize: 11,
            fontWeight: 500,
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: "#40404B",
            padding: "10px 15px",
            height: "32px",
          },
          grantTypeCheckBoxStyle: {
            "&$checked": {
              color: "#2FA86C",
            },
          },
          removeCredentialsButton: {
            backgroundColor: "#EA4C4D",
            "&:hover": {
              backgroundColor: "#EA4C4D",
            },
            textTransform: "capitalize",
            fontFamily: "GilmerMedium",
            lineHeight: "16px",
            color: "#FFFFFF",
          },
        },
        tryout: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          apiAccessTokenTitleText: {
            whiteSpace: "nowrap",
            margin: "auto auto auto 0px",
            textAlign: "left",
          },
          swaggerDownloadButton: {
            fontFamily: "GilmerMedium",
          },
          generateKeyButton: {
            fontFamily: "GilmerMedium",
          },
          warningIcon: {
            color: "#ff9a00",
            fontSize: 25,
            marginRight: 10,
          },
          helpIcon: {
            color: "#5567D5",
          },
          warningText: {
            fontSize: "13px",
          },
          warningLink: {
            textDecoration: "none",
            color: "black",
            opacity: 0.8,
            cursor: "pointer",
            "&:hover": {
              opacity: `1 !important`,
              textDecoration: "underline",
            },
          },
          applicationDropdownStyle: {
            backgroundColor: "#fff",
            boxSizing: "border-box",
            width: "auto",
            border: "1px solid #DEE0E7",
            borderRadius: "5px",
            boxShadow: "0 5px 10px -3px rgba(50,50,77,0.1)",
            color: "#222228",
            marginTop: "0.25rem",
          },
          divider: {
            color: "#ffffff",
          },
          textLabel: {
            fontFamily: "Gilmer",
            fontSize: "13px",
            color: "#1D2028",
            lineHeight: "24px",
            textAlign: "left",
          },
        },
        documentation: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          documentTypeTextStyle: {
            fontFamily: "GilmerMedium",
            lineHeight: "14px",
            letterSpacing: "normal",
            color: "#1D2028",
            fontWeight: "bold",
            fontSize: 13,
          },
          documentSummaryText: {
            fontWeight: "bold",
            textAlign: "left",
            lineHeight: "20px",
          },
          documentContentStyle: {
            backgroundColor: "#F7F8FB",
            border: "1px solid rgba(0, 0, 0, .125)",
            borderRadius: "5px",
          },
          documentAvailabilityText: {
            fontStyle: "Italic",
          },
          documentName: {
            textDecoration: "none",
            color: "black",
          },
          documentTypeBoxStyle: {
            paddingLeft: 0,
            paddingRight: 0,
          },
          documentNameListBoxStyle: {
            backgroundColor: "#767373",
          },
        },
        sdks: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          title: {
            fontFamily: "GilmerMedium",
            textAlign: "left",
            fontSize: "14px",
            lineHeight: "14px",
            letterSpacing: "normal",
            color: "#1D2028",
          },
          noSDKLabelStyle: {
            fontStyle: "italic",
            textAlign: "left",
          },
          SDKCardStyle: {
            background: "#FFFFFF",
            padding: "15px",
            borderRadius: "8px",
            boxShadow:
              "inset 0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.05)",
          },
          SDKLanguageStyle: {
            textTransform: "capitalize",
            fontFamily: "GilmerMedium",
            fontSize: "13px",
            lineHeight: "14px",
            letterSpacing: "normal",
            color: "#1D2028",
            paddingTop: "24px",
          },
        },
        contracts: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          title: {
            fontSize: "14px",
            fontFamily: "GilmerMedium",
            lineHeight: "14px",
            color: "#1D2028",
            letterSpacing: "normal",
          },
          warningIconStyle: {
            color: "#ff9a00",
            fontSize: 25,
            marginRight: 10,
          },
          GenerateCredentialsInfoLabel: {
            color: "#ffffff",
          },
          contractRadioButtonStyle: {
            color: "#ffffff",
          },
          contractLabelStyle: {
            color: `#ffffff`,
          },
          updateButtonStyle: {
            justifyContent: "flex-end",
            alignItems: "center",
          },
          updateButton: {
            fontSize: "13px",
            borderRadius: "25px",
            backgroundColor: "primary",
          },
        },
      },
    },
    applicationViewPage: {
      tabs: {
        overview: {
          styles: {
            backgroundColor: "#FFFFFF",
          },
          overviewRowKeyTextStyle: {
            fontSize: "13px",
            lineHeight: "14px",
            color: "#1D2028",
            letterSpacing: 0,
            fontFamily: "GilmerMedium",
          },
          overviewRowValueTextStyle: {
            fontSize: "13px",
            lineHeight: "40px",
            color: "#40404B",
            letterSpacing: 0,
            fontFamily: "Gilmer",
          },
          emailChipStyle: {
            border: "1px solid #0095FF",
            background: "#0095FF1A",
            color: "#0095FF",
            marginRight: "8px"          
          }
        },
        productionKeys: {
          oAuth: {
            styles: {
              backgroundColor: "#FFFFFF",
            },
            tabs: {
              root: {
                borderBottom: "1px solid #E4E8EE",
                marginBottom: "21px",
                height: "57px",
              },
              tabIndicator: {
                color: "#5567d5",
                backgroundColor: "#5567d5",
              },
              tabStyles: {
                padding: 0,
                fontFamily: "GilmerMedium",
                color: "#1D2028",
              },
              tabTitleTextStyles: {
                fontSize: "13px",
                textTransform: "initial",
              },
              selectedTabStyles: {
                color: "#5567d5 !important",
              },
            },
            subTitleTextStyles: {
              textAlign: "left",
              lineHeight: "14px",
              paddingBottom: "16px",
              fontFamily: "GilmerMedium",
              color: "#1D2028",
            },
            contentKeyColor: {
              color: "#222228",
            },
            noConsumerKeyTextStyle: {
              fontFamily: "Gilmer",
              lineHeight: "24px",
              color: "#8D91A3",
            },
            advancedConfigurationHeader: {
              backgroundColor: "#FFFFFF",
            },
            advancedConfigurationDetails: {
              backgroundColor: "#FFFFFF",
            },
            advancedConfigKeyStyle: {
              color: "#222228",
              paddingTop: "7px",
            },
            updateAdvancedConfigurationsButtonStyles: {
              textTransform: "capitalize",
              fontFamily: "GilmerMedium",
              fontSize: "13px",
              lineHeight: "16px",
              color: "#FFFFFF",
            },
            generateCredentialsButtonStyle: {
              textTransform: "capitalize",
              fontFamily: "GilmerMedium",
              fontSize: "13px",
              lineHeight: "16px",
              color: "#FFFFFF",
              margin: "0",
              position: "absolute",
              transform: "translateY(-50%)",
            },
            generateTokenButtonTextStyle: {
              textAlign: "center",
              fontFamily: "GilmerMedium",
              fontSize: "11px",
              fontWeight: 500,
              fontStretch: "normal",
              fontStyle: "normal",
              letterSpacing: "0",
              color: "#40404B",
            },
            clientAddEditButtonStyle: {
              textTransform: "capitalize",
              fontFamily: "GilmerMedium",
              fontSize: "13px",
              lineHeight: "16px",
              color: "#FFFFFF",
              margin: "0",
            },
            helperTextStyle: {
              color: "#8D91A3",
              lineHeight: 1.6,
            },
            errorTextStyle: {
              display: "flex",
              alignItems: "center",
              fontSize: 10.5,
            },
          },
        },
        subscriptions: {
          styles: {
            backgroundColor: "#ffffff",
          },
          pageTitle: {
            paddingLeft: 0,
            fontFamily: "GilmerMedium",
            textAlign: "left",
            color: "#181C20",
            fontSize: "17px",
            lineHeight: "24px",
          },
          addApiBtn: {
            fontSize: "13px",
            borderRadius: "25px",
            backgroundColor: "primary",
          },
          copyScopesBtn: {
            fontSize: "13px",
            borderRadius: "25px",
            backgroundColor: "primary",
            marginRight: "8px",
          },
        },
      },
    },
    elements: {
      status: {
        containerStyles: {
          backgroundColor: "#F7F8FB",
          borderRadius: 16,
        },
        textStyle: {
          textTransform: "capitalize",
          fontSize: 13,
          lineHeight: "16px",
          fontFamily: "Gilmer",
          fontWeight: "normal",
          color: "#32324D",
        },
        activeDotStyle: {
          backgroundColor: "#2e7d32",
        },
        stoppedDotStyle: {
          backgroundColor: "red",
        },
        pendingDotStyle: {
          backgroundColor: "#FFC107"
        },
        draftedDotStyle: {
          backgroundColor: "#898B8B",
        },
      },
      apiCard: {
        styles: {
          margin: "16px",
          padding: "16px",
          border: "1px solid #FFFFFF",
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          minHeight: "212px",
          minWidth: "276px",
          boxShadow: "0 6px 20px 0 rgba(0,0,0,0.02)",
          cursor: "pointer",
          "&:hover": {
            position: "absolute",
            zIndex: 1,
            border: "1px solid #DBDCDF",
            boxShadow: "0 15px 50px 0 rgba(0,0,0,0.15)",
            backgroundColor: "#FFFFFF",
            "& .api-card-content": {
              display: "block",
            },
            borderColor: "#5567D5"
          },
        },
        content: {
          textAlign: "left",
          fontSize: "13px",
          lineHeight: "24px",
          color: "#8D91A3",
        },
        icon: {
          width: "56px",
          height: "56px",
          backgroundColor: "#5567D5",
          fontFamily: "sans-serif",
        },
        title: {
          fontSize: "13px",
          lineHeight: "16px",
          color: "#1D2028",
          fontFamily: "GilmerMedium",
        },
        tryOutButton: {
          textTransform: "capitalize",
          fontSize: "11px",
          lineHeight: "13px",
          color: "#40404B",
          width: "82px",
          height: "32px",
          backgroundColor: "#f7f8fb",
          "&:hover": {
            fontWeight: "bold"
          }
        },
        docsButton: {
          marginRight: "16px",
          textTransform: "capitalize",
          fontSize: "11px",
          lineHeight: "13px",
          color: "#40404B",
          height: "32px",
          backgroundColor: "#ffffff",
          "&:hover": {
            backgroundColor: "#ffffff",
            fontWeight: "bold"
          }
        },
        apiType: {
          color: "#8D91A3"
        },
        lifecycleStatus: {
          color: "#1D2028"
        },
        versionWrapper: {
          color: "#8D91A3"
        },
        rating: {
          color: "#5567D5"
        }
      },
      apiInfoBar: {
        apiIconImage: {
          border: "1px solid #E6E7EC",
          borderRadius: "8px",
          boxSizing: "border-box",
          fontSize: "35px",
          backgroundColor: "#5567D5",
          textTransform: "capitalize",
          color: "#ffffff",
        },
        apiIconImageBackground: {
          backgroundColor: "#ffffff",
        },
        apiName: {
          color: "#181C20",
          fontSize: "19px",
          letterSpacing: 0,
          lineHeight: "24px",
          fontFamily: "GilmerMedium",
        },
        apiVersionProvider: {
          color: "#8D91A3",
          fontSize: "13px",
          letterSpacing: 1,
          lineHeight: "24px",
        },
        apiRatingText: {
          color: "#8D91A3",
          fontSize: "13px",
          letterSpacing: 1,
          lineHeight: "24px",
          textAlign: "center",
        },
        divider: {
          height: "1px",
          opacity: "52%",
          color: "#DCDEE4",
        },
      },
      appInfoBar: {
        appIconImage: {
          border: "1px solid #E6E7EC",
          borderRadius: "8px",
          boxSizing: "border-box",
          fontSize: "35px",
          backgroundColor: "#5567D5",
          color: "#ffffff",
        },
        appIconImageBackgroundColor: {
          backgroundColor: "#ffffff",
        },
        appNameStyles: {
          color: "#181C20",
          fontSize: "14px",
          letterSpacing: 0,
          lineHeight: "28px",
          fontFamily: "GilmerMedium",
        },
        appOwnerStyles: {
          color: "#8D91A3",
          fontSize: "13px",
          letterSpacing: 1,
          lineHeight: "24px",
        },
        editButtonStyles: {
          backgroundImage: `url('${editSvg}')`,
        },
        addButtonStyles: {
          backgroundImage: `url('${addSvg}')`,
        },
        permissionButtonStyles: {
          width: 'max-content',
        },
        outlineButtonStyles: {
          color: "#1976d2",
          border: "1px solid #CBCFF1",
          borderColor: "#CBCFF1",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left",
          "&:hover": {
            backgroundColor: "#F0F1FB",
            color: "#1976d2",
            borderColor: "#A6B3FF",
            boxShadow: "0 1px 2px 0 #E0E2E9",
          },
        },
        buttonHoverStyles: {
          color: "#1976d2",
          border: "1px solid",
          borderColor: "#CBCFF1",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left",
          "&:hover": {
            backgroundColor: "rgb(204, 209, 242, 0.3)",
            color: "#1976d2",
            borderColor: "#A6B3FF",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
          },
          "&:focus": {
            backgroundColor: `#1976d2 !important`,
            color: "#ffffff",
            borderColor: "#1976d2",
            outline: "none",
          },
        },
        deleteButtonStyles: {
          color: "#EA4C4D",
          borderRadius: "50%",
          border: "1px solid",
          borderColor: "#F8C7C7",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          background: `url('${deleteSvgIcon}')`,
          "&:hover": {
            backgroundColor: "#EA4C4D",
            color: "#EA4C4D",
            borderColor: "#EA4C4D",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            background: `url('${deleteWhiteSvgIcon}')`,
          },
          "&:focus, &:active": {
            borderColor: "#EA4C4D",
            backgroundColor: "#EA4C4D !important",
            color: "#ffffff",
            backgroundRepeat: "no-repeat",
            backgroundSize: "20px",
            backgroundPosition: "center",
            background: `url('${deleteWhiteSvgIcon}')`,
          },
        },
        divider: {
          height: "1px",
          opacity: "52%",
          color: "#DCDEE4",
        },
      },
      copyTextField: {
        borderStyle: {
          borderWidth: "1px",
          borderColor: "#ffffff !important",
        },
        textFieldStyle: {
          width: "auto",
          paddingRight: 0,
          paddingLeft: "10px",
          backgroundColor: "#F7F8FB",
          letterSpacing: 1,
          color: "#8D91A3",
          fontSize: "13px",
        },
        copyIconButtonStyle: {
          padding: 0,
        },
        copyTooltipStyle: {
          backgroundColor: "#4fafff",
          color: "#ffffff",
          fontSize: 11,
        },
      },
      textButton: {
        textButtonStyle: {
          textTransform: "capitalize",
          color: "#5567D5",
        },
      },
      greyButton: {
        textTransform: "capitalize",
        borderRadius: "5px",
        backgroundColor: "#f7f8fb",
        boxShadow:
          "inset 0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.05)",
        "&:hover": {
          backgroundColor: "#f7f8fb",
          boxShadow:
            "inset 0 0 0 1px rgba(0,0,0,0.06), 0 2px 4px 0 rgba(0,0,0,0.12)",
        },
        "&:focus, &:active": {
          backgroundColor: "#f7f8fb",
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0) 100%)",
          boxShadow:
            "inset 0 0 0 1px rgba(0,0,0,0.08), 0 0 0 0 rgba(0,0,0,0.07)",
        },
      },
    },
    noPermission: {
      styles: {
        fontFamily: "Gilmer",
        fontSize: "16px",
        lineHeight: "32px",
        color: "#40404B",
        textAlign: "center",
        marginBottom: "20px",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "782px",
      },
    },
    clientErrorPage: {
      signInContainer: {
        styles: {
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
      },
      signInText: {
        styles: {
          fontFamily: "GilmerBold",
          fontSize: "29px",
          marginTop: "50px",
        },
      },
      tryAgainBtn: {
        styles: {
          margin: "20px",
          textTransform: "capitalize",
        },
      },
      loaderContainer: {
        styles: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      goBackButton: {
        styles: {
          margin: '20px',
          width: '96px',
        },
      },
      alignCenter: {
        styles: {
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
  },
});
